/**
 * Editor-only accounts.
 *
 * An account whose role is exactly 'editor' was created by accepting an
 * editor invitation: it edits other people's projects and has no creator
 * features (library, projects, editors, billing). 'creator,editor' and
 * 'editor,creator' accounts are creators too; 'user', 'superadmin' and a
 * missing role are treated as creators. The API refuses creator actions for
 * editor-only accounts; the app keeps them off those pages.
 */

/** Where an editor-only account lives. */
export const EDITOR_HOME_PATH = '/edit-projects';

/** Shown on the editor dashboard after a creator-only page sent an editor there. */
export const CREATOR_ONLY_PAGE_NOTICE =
  'That page is for creators. Your PithPlay account is an editor account — your projects are here.';

/** Navigation state the editor dashboard reads to explain a redirect. */
export interface EditorHomeRedirectState {
  notice?: string;
}

/** The API's refusal of a creator action for an editor-only account. */
export const EDITOR_ACCOUNT_ERROR_MESSAGE =
  "This is an editor account. Creator features aren't available here.";

export const isEditorOnlyRole = (role?: string | null): boolean => role === 'editor';

const EDITOR_ACCOUNT_PATTERN = /this is an editor account/i;

/**
 * The API's "This is an editor account…" message when `error` is that
 * refusal (an ApolloError, a GraphQL error list, or a plain Error that
 * carried the server message), otherwise null.
 */
export const getEditorAccountErrorMessage = (error: unknown): string | null => {
  if (!error) return null;
  const candidate = error as {
    message?: unknown;
    graphQLErrors?: Array<{ message?: unknown; extensions?: { code?: unknown } }>;
    networkError?: { result?: { errors?: Array<{ message?: unknown }> } };
  };
  const messages: unknown[] = [
    ...(candidate.graphQLErrors ?? []).map((graphQLError) => graphQLError?.message),
    ...(candidate.networkError?.result?.errors ?? []).map((graphQLError) => graphQLError?.message),
    candidate.message,
  ];
  for (const message of messages) {
    if (typeof message === 'string' && EDITOR_ACCOUNT_PATTERN.test(message)) {
      // Plain Errors sometimes carry several joined messages; show just ours.
      const match = message.match(/this is an editor account[^;]*/i);
      return (match ? match[0] : message).trim();
    }
  }
  return null;
};
